import React, { useState } from 'react';
import Calling from '../api/Calling';
import logo from '../Image/logo.svg';
import { FaUserTimes, FaExclamationTriangle, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { MdEmail, MdDelete } from 'react-icons/md';
import { IoWarning } from 'react-icons/io5';

const DeleteUserPage = () => {
    const [userId, setUserId] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        const body = { u_email: userId, platform: "website" };

        try {
            const resp = await Calling.webUserDeleteAccount(body);
            if (resp.status) {
                setResponseMessage('Your account deletion request has been submitted successfully. Your account will be deleted within 48 hours.');
                setMessageType('success');
                setUserId(''); // Clear the input
                setTimeout(() => window.location.reload(), 5000);
            } else {
                setResponseMessage(resp.message || 'Failed to submit the deletion request.');
                setMessageType('error');
            }
        } catch (error) {
            setResponseMessage('An error occurred: ' + error.message);
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-main-th via-main-bg to-main-th flex items-center justify-center p-4">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-purple/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-main-yellow/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-40 left-20 w-80 h-80 bg-secondary-teal/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative bg-main-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-md transform transition-all hover:scale-[1.02]">
                {/* Logo and Title */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <div className="relative">
                            {/* Logo would go here */}
                            <img src={logo} alt='Quiz Loot' className='w-20 h-20 drop-shadow-lg' />

                        </div>
                    </div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-purple to-main-blue bg-clip-text text-transparent">
                        Quiz Loot
                    </h1>
                    <p className="text-main-subfont mt-2">Account Deletion Request</p>
                </div>

                {/* Warning Section */}
                <div className="bg-main-yellow/10 border border-main-yellow/30 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <IoWarning className="text-main-yellow text-xl mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                            <p className="font-semibold text-main-font mb-1">Important Notice:</p>
                            <ul className="text-main-subfont space-y-1">
                                <li>• All your quiz progress will be permanently deleted</li>
                                <li>• Your scores and achievements will be lost</li>
                                <li>• This action cannot be undone</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="user_id" className="block text-sm font-medium text-main-font mb-2">
                            Enter your registered email address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MdEmail className="text-main-subfont" />
                            </div>
                            <input
                                type="email"
                                id="user_id"
                                name="user_id"
                                required
                                placeholder="your.email@example.com"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                className="w-full pl-10 pr-3 py-3 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-delete focus:border-transparent transition-all"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-main-delete to-main-delete/90 text-white py-3 px-4 rounded-lg font-medium 
                                 hover:from-main-delete/90 hover:to-main-delete focus:outline-none focus:ring-2 focus:ring-main-delete focus:ring-offset-2 
                                 transform transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed
                                 flex items-center justify-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <MdDelete className="text-xl" />
                                Request Account Deletion
                            </>
                        )}
                    </button>
                </form>

                {/* Response Message */}
                {responseMessage && (
                    <div className={`mt-6 p-4 rounded-lg flex items-start gap-3 animate-fadeIn
                        ${messageType === 'success'
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-main-delete/10 border border-main-delete/30'}`}
                    >
                        {messageType === 'success' ? (
                            <FaCheckCircle className="text-green-500 text-xl mt-0.5 flex-shrink-0" />
                        ) : (
                            <FaExclamationTriangle className="text-main-delete text-xl mt-0.5 flex-shrink-0" />
                        )}
                        <div className={`text-sm ${messageType === 'success' ? 'text-green-800' : 'text-main-delete'}`}>
                            {responseMessage}
                        </div>
                    </div>
                )}

                {/* Footer Info */}
                <div className="mt-8 pt-6 border-t border-main-border">
                    <div className="flex items-start gap-2 text-xs text-main-subfont">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                        <p>
                            Need help? Contact our support team at support@quizloot.com
                        </p>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default DeleteUserPage;